// ═══ 검차 셸 A1 홈 (신고 371 · BUILD_PLAN §4) — 앱 simple_home_screen.dart 1:1 ═══
// 인사 → 전폭 CTA → (결정 대기 있으면 앰버 액션 카드) → 3칸 KPI → 검차정산 카드 → 진행 중 목록(최대 5).
// 데이터: 진입 시 listOrders({}) + companySettlement({month}) 각 1회. autodex:eval-updated 수신 시 목록만 재조회.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T, R, S } from '../../theme';
import { useT, useLocale } from '../../../i18n/I18nProvider';
import * as api from '../api';
import { groupOf, sortActive, orderTitle, orderSubtitle, fmtDate, currentMonth } from '../fmt';
import { describeSimpleError } from '../errors';
import { useIsDesktop } from '../useDesktop';
import HomeDesktop from './home/HomeDesktop';
import KpiTile from '../parts/KpiTile';
import SimpleCard from '../parts/SimpleCard';
import SimpleStatusPill from '../parts/SimpleStatusPill';
import { CancelReasonBadge, ReapplyLink } from '../parts/CancelReasonBits';
import MoneyKRW from '../parts/MoneyKRW';
import LtrText from '../parts/LtrText';
import ErrorText from '../parts/ErrorText';

var ctaStyle = {
  display: 'flex', alignItems: 'center', gap: 14, width: '100%', boxSizing: 'border-box',
  background: T.navy, border: '1px solid ' + T.navy, borderRadius: R.card, padding: '16px 18px',
  color: T.card, cursor: 'pointer', textAlign: 'start', fontFamily: 'inherit'
};
var ctaTextWrap = { flex: 1, minWidth: 0 };
var ctaTitle = { fontSize: 14.5, fontWeight: 800, letterSpacing: '-.2px' };
var ctaDesc = { fontSize: 12, opacity: .82, marginTop: 3 };
var ctaHint = { flex: 'none', fontSize: 11.5, fontWeight: 700, opacity: .85, whiteSpace: 'nowrap' };

var kpiRow = { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginTop: 14 };

var actionCard = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', boxSizing: 'border-box', marginTop: 12,
  background: T.warnTint, border: '1px solid ' + T.warnBd, borderRadius: R.card, padding: '12px 14px',
  color: T.warn, cursor: 'pointer', textAlign: 'start', fontFamily: 'inherit'
};
var actionTitle = { fontSize: 13, fontWeight: 800 };
var actionSub = { fontSize: 11.5, marginTop: 2, opacity: .85 };

var cardLinkBtn = { display: 'block', width: '100%', textAlign: 'start', border: 0, background: 'none', padding: 0, fontFamily: 'inherit', cursor: 'pointer' };
var settleBody = { padding: '13px 15px' };
var settleDesc = { fontSize: 11.5, color: T.muted };
var settleRow = { display: 'flex', alignItems: 'flex-end', gap: 16, marginTop: 11 };
var settleCol2 = { marginInlineStart: 'auto', textAlign: 'end' };
var settleLabel = { fontSize: 10.5, fontWeight: 700, color: T.faint };
var settleValue = { fontSize: 20, fontWeight: 800, letterSpacing: '-.4px', marginTop: 2, display: 'block' };

var listRow = { display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderTop: '1px solid ' + T.bdSoft, cursor: 'pointer' };
var listRowFirst = Object.assign({}, listRow, { borderTop: 0 });
var listTextWrap = { flex: 1, minWidth: 0 };
var listTitle = { fontSize: 13, fontWeight: 700, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
var listSub = { fontSize: 11.5, color: T.muted, marginTop: 2, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
var moreRow = { padding: '9px 14px', borderTop: '1px solid ' + T.bdSoft, textAlign: 'center' };
var moreLink = { fontSize: 12, fontWeight: 700, color: T.accentPress, background: 'none', border: 0, cursor: 'pointer', fontFamily: 'inherit', padding: 0 };

function SimpleHome(props) {
  var t = useT();
  var loc = useLocale();
  var navigate = useNavigate();
  var isDesktop = useIsDesktop();
  var user = props.user || {};
  var name = user.companyNameEn || user.companyName || user.name || '';

  var [ordersState, setOrdersState] = useState({ orders: [], loading: true, error: null });
  var [settleState, setSettleState] = useState({ data: null, loading: true, error: null });

  var loadOrders = useCallback(function () {
    setOrdersState(function (prev) { return { orders: prev.orders, loading: true, error: null }; });
    api.listOrders({}).then(function (res) {
      setOrdersState({ orders: (res && res.data) || [], loading: false, error: null });
    }).catch(function (e) {
      setOrdersState({ orders: [], loading: false, error: e });
    });
  }, []);

  useEffect(function () { loadOrders(); }, [loadOrders]);

  useEffect(function () {
    api.companySettlement({ month: currentMonth() }).then(function (res) {
      setSettleState({ data: { count: res && res.count, total: res && res.total }, loading: false, error: null });
    }).catch(function (e) {
      setSettleState({ data: null, loading: false, error: e });
    });
  }, []);

  useEffect(function () {
    var onUpdate = function () { loadOrders(); };
    window.addEventListener('autodex:eval-updated', onUpdate);
    return function () { window.removeEventListener('autodex:eval-updated', onUpdate); };
  }, [loadOrders]);

  var orders = ordersState.orders;
  var counts = { inProgress: 0, actionNeeded: 0, finished: 0 };
  orders.forEach(function (o) { counts[groupOf(o && o.status)] += 1; });
  var active = sortActive(orders);
  var visible = active.slice(0, 5);
  var moreCount = active.length - visible.length;

  // ≥1024 는 2열 워크벤치(§8). 그 아래(모바일·769~1023)는 아래 1열 트리를 한 줄도 바꾸지 않는다.
  if (isDesktop) {
    return (<HomeDesktop name={name} orders={orders} counts={counts} active={active}
      settle={settleState} ordersError={ordersState.error} onReload={loadOrders} onNavigate={navigate} />);
  }

  return (<div className="ss-home">
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 15.5, fontWeight: 800, letterSpacing: '-.3px', color: T.ink }}>{t('simpleB1Hello', { name: name })}</div>
      <div style={{ fontSize: 12, color: T.sub, marginTop: 3 }}>{t('simpleHelloSub')}</div>
    </div>

    <button type="button" style={ctaStyle} onClick={function () { navigate('/main/s/request'); }}>
      <div style={ctaTextWrap}>
        <div style={ctaTitle}>{t('simpleB1CtaTitleAction')}</div>
        <div style={ctaDesc}>{t('simpleB1CtaDesc')}</div>
      </div>
      <span className="ss-cta-hint" style={ctaHint}>{t('simpleB1CtaShort')}</span>
    </button>

    {ordersState.error ? <ErrorText>{describeSimpleError(t, ordersState.error)}</ErrorText> : null}

    {counts.actionNeeded > 0 ? (<button type="button" style={actionCard}
      onClick={function () { navigate('/main/s/requests?group=actionNeeded'); }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={actionTitle}>{t('simpleActionPending', { count: counts.actionNeeded })}</div>
        <div style={actionSub}>{t('simpleActionSub')}</div>
      </div>
    </button>) : null}

    <div style={kpiRow}>
      <KpiTile label={t('simpleGroupInProgress')} count={counts.inProgress} tone="accent"
        onClick={function () { navigate('/main/s/requests?group=inProgress'); }} />
      <KpiTile label={t('simpleGroupActionNeeded')} count={counts.actionNeeded} tone={counts.actionNeeded > 0 ? 'warn' : 'ink'}
        onClick={function () { navigate('/main/s/requests?group=actionNeeded'); }} />
      <KpiTile label={t('simpleGroupFinished')} count={counts.finished} tone="ink"
        onClick={function () { navigate('/main/s/requests?group=finished'); }} />
    </div>

    <div style={{ marginTop: 14 }}>
      <button type="button" style={cardLinkBtn} onClick={function () { navigate('/main/s/fees'); }}>
        <SimpleCard title={t('shellTabSettlement')}>
          <div style={settleBody}>
            <div style={settleDesc}>{t('simpleB1SettleDesc')}</div>
            {settleState.error ? <ErrorText>{describeSimpleError(t, settleState.error)}</ErrorText> : (<div style={settleRow}>
              <div>
                <div style={settleLabel}>{t('simpleFeeCountLabel')}</div>
                <LtrText style={settleValue}>{t('simpleInspectionsCount', { count: (settleState.data && settleState.data.count) || 0 })}</LtrText>
              </div>
              <div style={settleCol2}>
                <div style={settleLabel}>{t('simpleFeeTotal')}</div>
                <MoneyKRW won={(settleState.data && settleState.data.total) || 0} style={Object.assign({}, settleValue, { color: T.money })} />
              </div>
            </div>)}
          </div>
        </SimpleCard>
      </button>
    </div>

    <div style={{ marginTop: 14 }}>
      <SimpleCard title={t('simpleB1ActiveTitle')}>
        {visible.length === 0 ? <div style={S.emptyText}>{t('simpleB1ActiveEmpty')}</div> : (<div>
          {visible.map(function (o, i) {
            var amber = groupOf(o.status) === 'actionNeeded';
            var subtitle = orderSubtitle(t, o) || (fmtDate(loc.locale, o.created_at) + (o.location ? ' · ' + o.location : ''));
            return (<div key={o.id} style={Object.assign({}, i === 0 ? listRowFirst : listRow, amber ? { background: T.warnTint } : null)}
              onClick={function () { navigate('/main/s/requests/' + o.id); }}>
              <div style={listTextWrap}>
                <div style={listTitle}>{orderTitle(t, o)}</div>
                <LtrText style={listSub}>{subtitle}</LtrText>
                <ReapplyLink order={o} />
              </div>
              <CancelReasonBadge order={o} />
              <SimpleStatusPill status={o.status} />
            </div>);
          })}
          {moreCount > 0 ? (<div style={moreRow}>
            <button type="button" style={moreLink} onClick={function () { navigate('/main/s/requests'); }}>
              {t('simpleB1ActiveMore', { count: moreCount })}
            </button>
          </div>) : null}
        </div>)}
      </SimpleCard>
    </div>
  </div>);
}

export default SimpleHome;
